// "Recent Activity" widget uchun lokalizatsiya qilingan nisbiy vaqt
// (masalan "10 daqiqa oldin" / "10 минут назад" / "10 min ago").
// Backenddagi i18n.py konventsiyasi bilan bir xil: uz/ru/en, topilmasa
// (yoki lang noto'g'ri bo'lsa) uz'ga qaytadi.
export type Lang = 'uz' | 'ru' | 'en';

// Rus tilida son oxiriga qarab uchta shakldan biri tanlanadi
// (1 daqiqa / 2 daqiqa / 5 daqiqa - "минута"/"минуты"/"минут").
// Standart rus tili ko'plik qoidasi.
function ruPlural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}

const MONTH_ABBR: Record<Lang, string[]> = {
  uz: ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avg', 'sen', 'okt', 'noy', 'dek'],
  ru: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

const JUST_NOW: Record<Lang, string> = {
  uz: 'hozirgina', ru: 'только что', en: 'just now',
};

function normalizeLang(lang: string): Lang {
  return lang === 'ru' || lang === 'en' ? lang : 'uz';
}

/** ISO 8601 vaqt belgisini (backend `timestamp` maydoni) hozirgi vaqtga
 * nisbatan o'qiladigan matnga aylantiradi. 7 kundan oshsa qisqa sana
 * (masalan "20 iyul") ko'rsatiladi - "N kun oldin" cheksiz o'sib
 * ma'nosizlashib ketmasligi uchun. */
export function formatRelativeTime(iso: string, langInput: string = 'uz'): string {
  const lang = normalizeLang(langInput);
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));

  if (diffSec < 60) return JUST_NOW[lang];

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    if (lang === 'uz') return `${diffMin} daqiqa oldin`;
    if (lang === 'ru') return `${diffMin} ${ruPlural(diffMin, ['минута', 'минуты', 'минут'])} назад`;
    return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`;
  }

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    if (lang === 'uz') return `${diffHour} soat oldin`;
    if (lang === 'ru') return `${diffHour} ${ruPlural(diffHour, ['час', 'часа', 'часов'])} назад`;
    return `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`;
  }

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) {
    if (lang === 'uz') return `${diffDay} kun oldin`;
    if (lang === 'ru') return `${diffDay} ${ruPlural(diffDay, ['день', 'дня', 'дней'])} назад`;
    return `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`;
  }

  const d = new Date(iso);
  return `${d.getDate()} ${MONTH_ABBR[lang][d.getMonth()]}`;
}

const DUE_LABELS: Record<Lang, { overdue: (n: number) => string; today: string; tomorrow: string }> = {
  uz: {
    overdue: (n) => n === 1 ? 'Kecha' : `${n} kun oldin`,
    today: 'Bugun', tomorrow: 'Ertaga',
  },
  ru: {
    overdue: (n) => n === 1 ? 'Вчера' : `${n} ${ruPlural(n, ['день', 'дня', 'дней'])} назад`,
    today: 'Сегодня', tomorrow: 'Завтра',
  },
  en: {
    overdue: (n) => n === 1 ? 'Yesterday' : `${n} days ago`,
    today: 'Today', tomorrow: 'Tomorrow',
  },
};

export interface DueDateInfo {
  text: string;
  /** true bo'lsa muddat allaqachon o'tgan (qizil rangda ko'rsatiladi) */
  overdue: boolean;
  /** true bo'lsa muddat bugun (alohida ta'kidlanadi) */
  today: boolean;
}

/** "Task & Reminders" vazifasi uchun muddat sanasini (YYYY-MM-DD, backend
 * `due_date`) "Bugun"/"Ertaga"/"N kun oldin"/qisqa sana ko'rinishiga
 * o'giradi. Solishtirish kalendar kuni bo'yicha (soat/vaqt mintaqasi
 * e'tiborga olinmaydi - due_date backendda sanaga xos maydon). */
export function formatDueDate(dueDateStr: string, langInput: string = 'uz'): DueDateInfo {
  const lang = normalizeLang(langInput);
  const [y, m, d] = dueDateStr.split('-').map(Number);
  const due = new Date(y, (m || 1) - 1, d || 1);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);

  if (diffDays === 0) return { text: DUE_LABELS[lang].today, overdue: false, today: true };
  if (diffDays === 1) return { text: DUE_LABELS[lang].tomorrow, overdue: false, today: false };
  if (diffDays < 0) return { text: DUE_LABELS[lang].overdue(-diffDays), overdue: true, today: false };

  return { text: `${due.getDate()} ${MONTH_ABBR[lang][due.getMonth()]}`, overdue: false, today: false };
}
